import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/software", label: "Software" },
  { to: "/tools-tech", label: "Tools & Tech" },
  { to: "/comparisons", label: "Comparisons" },
  { to: "/guides", label: "Guides" },
  { to: "/about", label: "About" },
];

export function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="App min-h-screen flex flex-col bg-[#F7F7F5]">
      {/* top disclosure bar */}
      <div className="bg-stone-900 text-stone-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-1.5">
          <p className="font-mono-pm text-[10px] tracking-wide text-center sm:text-left">
            Independent, editorially-driven reviews. <span className="text-[#CCFF00]">Some links are affiliate links.</span>
          </p>
        </div>
      </div>

      {/* header */}
      <header className="border-b-2 border-stone-900 bg-[#F7F7F5] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo-link" className="flex flex-col leading-none">
              <span className="font-serif-display text-2xl sm:text-3xl font-black tracking-tight text-stone-900">The PM Test</span>
              <span className="font-mono-pm text-[9px] sm:text-[10px] tracking-[0.25em] uppercase text-[#991B1B] mt-0.5">Does it pass The PM Test?</span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={({ isActive }) =>
                    `font-mono-pm text-xs tracking-widest uppercase px-3 py-2 transition-colors ${isActive ? "text-stone-900 border-b-2 border-[#991B1B]" : "text-stone-500 hover:text-stone-900"}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/share-your-stack" data-testid="nav-share-cta"
                className="ml-3 font-mono-pm text-xs tracking-widest uppercase bg-stone-900 text-white px-4 py-2.5 hover:bg-[#991B1B] transition-colors">
                Share Your Stack
              </Link>
            </nav>

            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-stone-900 bg-[#F7F7F5]" data-testid="mobile-menu">
            <div className="px-4 py-4 flex flex-col">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                  className={({ isActive }) => `font-mono-pm text-sm tracking-widest uppercase py-3 border-b border-stone-200 ${isActive ? "text-[#991B1B]" : "text-stone-700"}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/share-your-stack" onClick={() => setOpen(false)} className="mt-4 text-center font-mono-pm text-sm tracking-widest uppercase bg-stone-900 text-white px-4 py-3">
                Share Your Stack
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* footer */}
      <footer className="border-t-2 border-stone-900 bg-stone-900 text-stone-300 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
          <div className="mb-8"><AffiliateDisclosure compact /></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="font-serif-display text-2xl font-black text-white">The PM Test</div>
              <p className="font-mono-pm text-[10px] tracking-[0.2em] uppercase text-[#CCFF00] mt-1">Does it pass The PM Test?</p>
              <p className="text-sm text-stone-400 mt-4 leading-relaxed">Independent reviews and real-world comparisons of property management technology.</p>
            </div>
            <FooterCol title="Reviews" links={[["Software", "/software"], ["Tools & Tech", "/tools-tech"], ["Comparisons", "/comparisons"], ["Guides", "/guides"]]} />
            <FooterCol title="Company" links={[["About", "/about"], ["Share Your Stack", "/share-your-stack"], ["Editorial Policy", "/editorial-policy"]]} />
            <FooterCol title="Legal" links={[["Privacy Policy", "/privacy-policy"], ["Terms of Use", "/terms-of-use"], ["Affiliate Disclosure", "/affiliate-disclosure"]]} />
          </div>
          <div className="mt-10 pt-6 border-t border-stone-700 flex flex-col sm:flex-row justify-between gap-3">
            <p className="font-mono-pm text-[11px] text-stone-500">© {new Date().getFullYear()} The PM Test. All rights reserved.</p>
            <p className="font-mono-pm text-[11px] text-stone-500">thepmtest.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <h4 className="font-mono-pm text-[10px] tracking-[0.2em] uppercase text-stone-500 mb-4">{title}</h4>
      <ul className="space-y-2">
        {links.map(([label, to]) => (
          <li key={to}><Link to={to} className="text-sm text-stone-300 hover:text-[#CCFF00] transition-colors">{label}</Link></li>
        ))}
      </ul>
    </div>
  );
}
